import { BLANK, removeUndefined } from "../utils/global";
import { AccountData, AccountRole, SiteData } from "./account";

export enum TestkitStatusAPI {
  NEW           = 'NEW',
  STOCK         = 'STOCK',
  ALLOCATED     = 'ALLOCATED',
  ISSUED        = 'ISSUED',
  DONE          = 'DONE',
  DISCARDED     = 'DISCARDED',
}

export enum TestkitStatusUI {
  NEW           = 'NEW',
  STOCK         = 'STOCK',
  ALLOCATED     = 'ALLOCATED',
  DISPATCHED    = 'DISPATCHED',
  ISSUED        = 'ISSUED',
  DONE          = 'DONE',
  DISCARDED     = 'DISCARDED',
}

export const ADMIN_STATUSES = [
  TestkitStatusUI.NEW,
  TestkitStatusUI.STOCK,
  TestkitStatusUI.ALLOCATED,
  TestkitStatusUI.DISPATCHED,
  TestkitStatusUI.ISSUED,
  TestkitStatusUI.DONE,
  TestkitStatusUI.DISCARDED,
]

export const MIDWIFE_STATUSES = [
  TestkitStatusUI.ALLOCATED,
  TestkitStatusUI.DISPATCHED,
  TestkitStatusUI.ISSUED,
  TestkitStatusUI.DONE,
  TestkitStatusUI.DISCARDED,
]

export const getTestkitStatusLabel = (kit?: Testkit) => {
  switch (kit?.status) {
    case TestkitStatusAPI.NEW:
      return 'New';
    case TestkitStatusAPI.STOCK:
      return 'Stock';
    case TestkitStatusAPI.ALLOCATED:
      return !!kit.dispatched ? 'Dispatched' : 'Allocated';
    case TestkitStatusAPI.ISSUED:
      return 'Issued';
    case TestkitStatusAPI.DONE:
      return 'Finished';
    case TestkitStatusAPI.DISCARDED:
      return 'Discarded';
    default:
      return BLANK;
  }
}

export const getKitStatusUILabel = (status?: TestkitStatusUI) => {
  switch (status) {
    case TestkitStatusUI.NEW:
      return 'New';
    case TestkitStatusUI.STOCK:
      return 'Stock';
    case TestkitStatusUI.ALLOCATED:
      return 'Allocated';
    case TestkitStatusUI.DISPATCHED:
      return 'Dispatched';
    case TestkitStatusUI.ISSUED:
      return 'Issued';
    case TestkitStatusUI.DONE:
      return 'Finished';
    case TestkitStatusUI.DISCARDED:
      return 'Discarded';
    default:
      return BLANK;
  }
}

export const TestkitStatusPriority = {
  [TestkitStatusAPI.ALLOCATED]: 5,
  [TestkitStatusAPI.STOCK]: 4,
  [TestkitStatusAPI.NEW]: 3,
  [TestkitStatusAPI.ISSUED]: 2,
  [TestkitStatusAPI.DONE]: 1,
  [TestkitStatusAPI.DISCARDED]: 0,
}

export type StatusTransition = {
  status: TestkitStatusAPI,
  timestamp: string,      // ISO DateTime
}

export type Testkit = {
  objectType: 'testkit',
  dummyFlag: boolean,
  id: string,             // 123-456-789
  serial: number,
  patient?: string,       // P-123-456-7889
  midwife?: string,
  site?: string,

  status: TestkitStatusAPI,
  transitions: StatusTransition[],
  dispatched?: string,
  
  manufacturer?: string,
  paperLot?: string,
  paperExpiry?: string,             // Date
  swabLot?: string,
  swabExpiry?: string,              // Date
  additionalPaperLot?: string,
  additionalPaperExpiry?: string,   // Date
  additionalSwabLot?: string,
  additionalSwabExpiry?: string,    // Date
}

export const getDispatchedFilterOverride = (filter: TestkitFilterUI) => {
  const status = filter.status;

  if (status) {
    switch (status) {
      case TestkitStatusUI.NEW:
      case TestkitStatusUI.STOCK:
      case TestkitStatusUI.ALLOCATED:
        // Do not show dispatched kits
        return false;
      case TestkitStatusUI.DISPATCHED:
      case TestkitStatusUI.ISSUED:
      case TestkitStatusUI.DONE:
        // Only show dispatched kits
        return true;
      case TestkitStatusUI.DISCARDED:
        // Show dispatched & not dispatched
        return null;
    }
  }
  else {
    return undefined;
  }
}

export const getCompletedFilterOverride = (filter: TestkitFilterUI) => {
  const status = filter.status;

  if (!status) {
    return undefined;
  }
  else {
    return status === TestkitStatusUI.DONE || status === TestkitStatusUI.DISCARDED;
  }
}

export type TestkitFilterAPI = {
  idPattern?: string,
  siteId?: string,
  status?: TestkitStatusAPI,
  midwife?: string,
  manufacturer?: string,
  dispatched?: boolean,
  includeCompleted?: boolean,
}

export type TestkitFilterUI = {
  idPattern?: string,
  status?: TestkitStatusUI | null,
  site?: SiteData,
  midwife?: AccountData,
  manufacturer?: string,
  incDispatched?: boolean,
  incFinished?: boolean,
}

export const convertFilterToAPI = (filter: TestkitFilterUI, role?: AccountRole) => {
  const overrideDispatched  = getDispatchedFilterOverride(filter);
  const overrideCompleted   = getCompletedFilterOverride(filter);

  let dispatched: boolean | undefined;

  // If override is defined, use it (where null -> undefined to include all kits)
  if (overrideDispatched !== undefined) {
    dispatched = (overrideDispatched === null) ? undefined : overrideDispatched;
  }
  // ADMIN -> interpret user-defined filter (where true -> undefined to include all kits)
  // POWER_MEDIC & MEDICS -> see dispatched & non-dispatched (allocated) kits
  else {
    dispatched = (filter.incDispatched || role !== AccountRole.ADMIN) ? undefined : false;
  }

  return removeUndefined({
    idPattern: filter.idPattern || undefined,
    status: filter.status === TestkitStatusUI.DISPATCHED ? TestkitStatusAPI.ALLOCATED : (filter.status || undefined),
    siteId: filter.site?.id || undefined,
    midwife: filter.midwife?.username || undefined,
    manufacturer: filter.manufacturer || undefined,
    dispatched,
    // includeCompleted: filter.status === TestkitStatusUI.DONE || filter.incFinished || undefined,
    includeCompleted: overrideCompleted ?? (filter.incFinished || undefined),
  } as TestkitFilterAPI) as TestkitFilterAPI;
}

export type TestkitSorterAPI = {
  statusSort?: boolean,
  descending?: boolean,
}

export type TestkitSorterUI = {
  column: 'id' | 'status',
  desc: boolean,
}

export const convertSorterToAPI = (sorter: TestkitSorterUI) => {
  return removeUndefined({
    statusSort: sorter.column === 'status' ? true : undefined,
    descending: sorter.desc ? true : undefined,
  } as TestkitSorterAPI) as TestkitSorterAPI;
}